import type { ProductCenterProductRow, ProductCenterSort } from "@adpilot/shared";
import { IconPhoto } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/patterns/DataTable";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFormatters } from "@/lib/format";
import {
  type ScopeAccountMix,
  type SpApiMetric,
  scopeMetricAvailability,
  spApiMetricAvailability,
} from "@/lib/vendor-metric-availability";
import { AdsMetricCell, SellerMetricCell, SortHeader, VendorMetricCell } from "./ProductTableCells";

type Row = ProductCenterProductRow;
type MeasuredSeller = Exclude<Row["seller"], { status: "unavailable" }>;

interface SellerColumn {
  id: string;
  label: string;
  width: string;
  /** SP-API source; decides whether a vendor (1P) row can ever show it. */
  metric: SpApiMetric;
  value: (row: Row) => string | null;
  unavailable?: (row: Row) => boolean;
  unavailableLabel?: string;
}

export function ProductTable({
  rows,
  currency,
  tacosEligible = false,
  accountMix = "seller",
  vendorShopIds,
  sortBy,
  sortDirection,
  onSortChange,
}: {
  rows: ProductCenterProductRow[];
  currency: string;
  tacosEligible?: boolean;
  /**
   * Seller / vendor mix of the scope (#675). A vendor-only scope hides the
   * columns Amazon never reports for vendors; vendor rows explain the rest.
   */
  accountMix?: ScopeAccountMix;
  /** Vendor (1P) shops in a mixed scope, matched against `row.shop.id`. */
  vendorShopIds?: ReadonlySet<string>;
  sortBy: ProductCenterSort;
  sortDirection: "asc" | "desc";
  onSortChange: (sortBy: ProductCenterSort, direction: "asc" | "desc") => void;
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const { formatCurrency, formatNumber, formatPercent } = useFormatters();
  const numericColumns: Array<{ key: ProductCenterSort; label: string }> = [
    { key: "impressions", label: t("table.impressions") },
    { key: "clicks", label: t("table.clicks") },
    { key: "spend", label: t("table.spend") },
    { key: "adSales", label: t("table.adSales") },
    { key: "orders", label: t("table.orders") },
    { key: "acos", label: t("table.acos") },
  ];
  const isVendorRow = (row: Row) =>
    accountMix === "vendor" || (row.shop != null && (vendorShopIds?.has(row.shop.id) ?? false));
  const measured = (row: Row, format: (seller: MeasuredSeller) => string | null) =>
    row.seller.status === "unavailable" ? null : format(row.seller);
  const rowTacosEligible = (row: Row) => row.tacosEligible ?? tacosEligible;
  const allSellerColumns: SellerColumn[] = [
    {
      id: "totalSales",
      label: t("table.totalSales"),
      width: "min-w-[116px]",
      metric: "totalSales",
      value: (row) => measured(row, (seller) => formatCurrency(seller.sales, currency)),
    },
    {
      id: "sellerOrders",
      label: t("table.sellerOrders"),
      width: "min-w-[104px]",
      metric: "totalOrders",
      value: (row) =>
        measured(row, (seller) => formatNumber(seller.orders, { maximumFractionDigits: 0 })),
    },
    {
      id: "units",
      label: t("table.units"),
      width: "min-w-[96px]",
      metric: "totalOrders",
      value: (row) =>
        measured(row, (seller) => formatNumber(seller.units, { maximumFractionDigits: 0 })),
    },
    {
      id: "sessions",
      label: t("table.sessions"),
      width: "min-w-[104px]",
      metric: "sessions",
      value: (row) =>
        measured(row, (seller) => formatNumber(seller.sessions, { maximumFractionDigits: 0 })),
    },
    {
      id: "pageViews",
      // Vendors only ever get glance views (vendor traffic report / Data Kiosk).
      label: accountMix === "vendor" ? t("table.glanceViews") : t("table.pageViews"),
      width: "min-w-[104px]",
      metric: "organicTraffic",
      value: (row) =>
        measured(row, (seller) => formatNumber(seller.pageViews, { maximumFractionDigits: 0 })),
    },
    {
      id: "buyBox",
      label: t("table.buyBox"),
      width: "min-w-[104px]",
      metric: "buyBox",
      value: (row) =>
        measured(row, (seller) =>
          seller.buyBoxPercentage == null ? null : formatPercent(seller.buyBoxPercentage, 2),
        ),
    },
    {
      id: "conversionRate",
      label: t("table.conversionRate"),
      width: "min-w-[104px]",
      metric: "sessionConversion",
      value: (row) =>
        measured(row, (seller) =>
          seller.conversionRate == null ? null : formatPercent(seller.conversionRate, 2),
        ),
    },
    {
      id: "tacos",
      label: t("table.tacos"),
      width: "min-w-[90px]",
      metric: "tacos",
      value: (row) =>
        rowTacosEligible(row) && row.seller.status === "available" && row.seller.sales > 0
          ? formatPercent(row.ads.spend / row.seller.sales, 2)
          : null,
      unavailable: (row) => row.seller.status === "unavailable" || !rowTacosEligible(row),
      unavailableLabel: t("performance.tacosUnavailableLabel"),
    },
  ];
  const sellerColumns = allSellerColumns.filter(
    (column) => scopeMetricAvailability(accountMix, column.metric) !== "never",
  );

  return (
    <DataTable
      density="rich"
      stickyHeader
      className={sellerColumns.length < 8 ? "min-w-[1840px]" : "min-w-[2050px]"}
    >
      <TableHeader>
        <TableRow>
          <TableHead className="sticky left-0 z-20 min-w-[330px] border-r border-line bg-subtle">
            <SortHeader
              column="identity"
              label={t("table.product")}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          </TableHead>
          <TableHead className="min-w-[190px]">{t("table.catalog")}</TableHead>
          <TableHead className="min-w-[110px]">{t("table.productLine")}</TableHead>
          {numericColumns.map((column) => (
            <TableHead key={column.key} className="min-w-[104px] text-right">
              <SortHeader
                column={column.key}
                label={column.label}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
                align="end"
              />
            </TableHead>
          ))}
          {sellerColumns.map((column) => (
            <TableHead
              key={column.id}
              data-column={column.id}
              className={`${column.width} text-right`}
            >
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.shop?.id ?? ""}:${row.identity.dimension}:${row.identity.key}`}>
            <TableCell className="sticky left-0 z-10 border-r border-line bg-surface">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-line bg-subtle">
                  {row.identity.imageUrl ? (
                    <img
                      src={row.identity.imageUrl}
                      alt=""
                      className="size-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <IconPhoto className="size-5 text-muted-foreground/50" aria-hidden />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span
                    className="max-w-[240px] truncate text-[11px] font-semibold text-ink"
                    title={
                      row.identity.dimension === "PRODUCT_LINE" && row.identity.key === "unassigned"
                        ? t("toolbar.unassigned")
                        : (row.identity.title ?? row.identity.key)
                    }
                  >
                    {row.identity.dimension === "PRODUCT_LINE" && row.identity.key === "unassigned"
                      ? t("toolbar.unassigned")
                      : (row.identity.title ?? row.identity.key)}
                  </span>
                  <span className="max-w-[240px] truncate font-mono text-[9px] text-muted-foreground">
                    {identityLine(row, t)}
                  </span>
                  {row.shop ? (
                    <span className="text-[10px] text-muted-foreground">
                      {[row.shop.name, row.shop.marketplaceName].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                  {row.identity.brand || row.identity.category ? (
                    <span className="max-w-[240px] truncate text-[9px] text-muted-foreground">
                      {[row.identity.brand, row.identity.category].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex flex-col items-start gap-1">
                <Badge variant={row.identity.isActive ? "success" : "neutral"} size="compact">
                  {t(row.identity.isActive ? "table.active" : "table.inactive")}
                </Badge>
                <Badge
                  variant={row.identity.productStatus === "advertised" ? "info" : "neutral"}
                  size="compact"
                >
                  {t(`table.${row.identity.productStatus}`)}
                </Badge>
                <span className="max-w-[130px] truncate text-[9px] text-muted-foreground">
                  {row.identity.availability ?? t("table.unknown")}
                </span>
                {row.identity.priceAmount != null ? (
                  <span className="font-mono text-[10px] font-medium text-ink">
                    {formatCurrency(
                      row.identity.priceAmount,
                      row.identity.priceCurrency ?? currency,
                    )}
                  </span>
                ) : null}
                {isVendorRow(row) ? (
                  <span
                    data-slot="vendor-inventory-pending"
                    className="max-w-[170px] truncate text-[9px] text-muted-foreground"
                    title={t("common:vendorData.pendingTitle")}
                  >
                    {t("common:vendorData.inventoryPending")}
                  </span>
                ) : (
                  <span className="max-w-[170px] truncate text-[9px] text-muted-foreground">
                    {[
                      row.identity.fulfillmentChannel,
                      row.identity.quantity == null
                        ? null
                        : t("table.inventory", { count: row.identity.quantity }),
                    ]
                      .filter(Boolean)
                      .join(" · ") || t("table.inventoryUnavailable")}
                  </span>
                )}
                {row.identity.listingStatus.length > 0 ? (
                  <span
                    className="max-w-[170px] truncate text-[9px] text-muted-foreground"
                    title={row.identity.listingStatus.join(", ")}
                  >
                    {row.identity.listingStatus.join(" · ")}
                  </span>
                ) : null}
                {row.identity.bestSellerRank ? (
                  <span className="max-w-[170px] truncate text-[9px] text-muted-foreground">
                    {t("table.bestSellerRank", { rank: row.identity.bestSellerRank })}
                  </span>
                ) : null}
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">{row.productLine?.name ?? "—"}</TableCell>
            <AdsMetricCell status={row.ads.status}>
              {formatNumber(row.ads.impressions, { maximumFractionDigits: 0 })}
            </AdsMetricCell>
            <AdsMetricCell status={row.ads.status}>
              {formatNumber(row.ads.clicks, { maximumFractionDigits: 0 })}
            </AdsMetricCell>
            <AdsMetricCell status={row.ads.status}>
              {formatCurrency(row.ads.spend, currency)}
            </AdsMetricCell>
            <AdsMetricCell status={row.ads.status}>
              {formatCurrency(row.ads.adSales, currency)}
            </AdsMetricCell>
            <AdsMetricCell status={row.ads.status}>
              {formatNumber(row.ads.orders, { maximumFractionDigits: 0 })}
            </AdsMetricCell>
            <AdsMetricCell status={row.ads.status}>
              {row.ads.acos == null ? "—" : formatPercent(row.ads.acos, 2)}
            </AdsMetricCell>
            {sellerColumns.map((column) => {
              const availability = isVendorRow(row)
                ? spApiMetricAvailability("vendor", column.metric)
                : "available";
              if (availability !== "available") {
                return <VendorMetricCell key={column.id} availability={availability} />;
              }
              return (
                <SellerMetricCell
                  key={column.id}
                  value={column.value(row)}
                  unavailable={column.unavailable?.(row) ?? row.seller.status === "unavailable"}
                  partial={row.seller.status === "partial"}
                  unavailableLabel={column.unavailableLabel}
                />
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
}

export function ProductTableSkeleton(): React.ReactElement {
  const rows = ["row-a", "row-b", "row-c", "row-d", "row-e", "row-f"];
  return (
    <div className="flex min-h-[300px] flex-col" role="status" aria-label="Loading product data">
      <div className="flex h-9 items-center gap-4 border-b border-line bg-subtle px-3.5">
        <Skeleton className="h-3 w-64" />
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-3 flex-1" />
      </div>
      {rows.map((row) => (
        <div key={row} className="flex min-h-[72px] items-center gap-4 border-b border-line px-3.5">
          <Skeleton className="size-12 rounded-[8px]" />
          <div className="flex w-64 flex-col gap-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-2.5 w-2/3" />
          </div>
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-3 flex-1" />
        </div>
      ))}
    </div>
  );
}

function identityLine(
  row: ProductCenterProductRow,
  t: (key: string, options?: { value: string }) => string,
): string {
  return [
    row.identity.parentAsin ? t("identity.parentAsin", { value: row.identity.parentAsin }) : null,
    row.identity.asin ? t("identity.asin", { value: row.identity.asin }) : null,
    row.identity.sku ? t("identity.sku", { value: row.identity.sku }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}
